import React, {
  forwardRef, useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';

// Lista desplegable compartida de VLS: reemplaza el <select> nativo (que en
// macOS abre el menú gris del sistema) por una con el estilo del hub.
// Patrón combobox "select-only" de ARIA APG: el botón es role="combobox" y
// la lista role="listbox"; con buscador, el campo de búsqueda toma el foco y
// hace de combobox editable sobre la misma lista.
//
// ─── Migración desde <select> (mecánica) ──────────────────────────────────
//   ANTES:
//     <select id="x" value={v} onChange={(e) => setV(e.target.value)}
//             className="w-full px-3 py-2 border rounded-md ...">
//       <option value="" disabled>Seleccione…</option>
//       <optgroup label="Sección"><option value="a">A</option></optgroup>
//     </select>
//   DESPUÉS:
//     <Select id="x" value={v} onChange={(val) => setV(val)} className="w-full">
//       <option value="" disabled>Seleccione…</option>
//       <optgroup label="Sección"><option value="a">A</option></optgroup>
//     </Select>
//
//  1. `onChange` recibe el VALOR, no el evento: `e.target.value` → `val`.
//     Con hijos <option> el valor es string (igual que el select nativo); con
//     la prop `options` es el `value` original del objeto (puede ser número).
//  2. `className` es solo de layout (ancho, margen, flex). El aspecto (borde,
//     alto, padding, radio, color) lo pone el componente: quitar px/py/border/
//     rounded/bg/text-* del select viejo.
//  3. Los hijos <option>/<optgroup> se leen tal cual (también .map() adentro).
//     Un <option value="" disabled> se toma como placeholder (no se lista).
//     Un <option value=""> habilitado ("Todos") sí es una opción normal.
//     Alternativa: `options={[{ value, label, disabled?, group? }]}`.
//  4. `defaultValue` sin `value` = no controlado (igual que el nativo).
//  5. `ref` apunta al botón (sirve para .focus()).
//  6. `<label htmlFor={id}>` funciona: nombra al botón y al hacer clic lo abre.
//
// Otras props: placeholder, disabled, required (mensaje accesible propio),
// name (input oculto para formularios nativos), size 'sm' (40 px) | 'md'
// (44 px), searchable (por defecto: más de 8 opciones), clearable (fila
// "Quitar selección" y tecla Supr/Retroceso), aria-label, aria-labelledby,
// aria-describedby, title (título de la hoja en móvil; por defecto el texto
// del <label>), zIndexClass (capa del panel; por defecto
// z-index: var(--vls-select-z, 1000), por encima del Modal compartido).
//
// Sin dependencias. Colores por var(--color-*) con respaldo, y medidas en
// estilo en línea: las apps tienen escalas de spacing distintas (en horario
// px-1 son 8 px), así que ninguna clase de spacing decide el tamaño.

const C = {
  card: 'var(--color-popover, var(--color-card, #FFFFFF))',
  fg: 'var(--color-foreground, #1C1917)',
  muted: 'var(--color-muted, #F5F5F4)',
  mutedFg: 'var(--color-muted-foreground, #78716C)',
  border: 'var(--color-border, #E7E5E4)',
  input: 'var(--color-input, var(--color-border, #E7E5E4))',
  primary: 'var(--color-primary, #0F7A3D)',
  error: 'var(--color-error, #DC2626)',
  bg: 'var(--color-background, #FFFFFF)',
};
const suave = (pct) => `color-mix(in srgb, ${C.primary} ${pct}%, transparent)`;

const BUSCADOR_DESDE = 8;
const MQ_MOVIL = '(max-width: 639px)';

const normalizar = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function textoDe(nodo) {
  if (nodo == null || typeof nodo === 'boolean') return '';
  if (typeof nodo === 'string' || typeof nodo === 'number') return String(nodo);
  if (Array.isArray(nodo)) return nodo.map(textoDe).join('');
  if (React.isValidElement(nodo)) return textoDe(nodo.props.children);
  return '';
}

// Hijos <option>/<optgroup> → [{ value, label, disabled, group }].
function opcionesDeHijos(children, group, groupDisabled, out) {
  React.Children.forEach(children, (hijo) => {
    if (!React.isValidElement(hijo)) return;
    if (hijo.type === React.Fragment) { opcionesDeHijos(hijo.props.children, group, groupDisabled, out); return; }
    if (hijo.type === 'optgroup') {
      opcionesDeHijos(hijo.props.children, hijo.props.label, groupDisabled || !!hijo.props.disabled, out);
      return;
    }
    if (hijo.type === 'option') {
      const label = textoDe(hijo.props.children);
      out.push({
        value: hijo.props.value !== undefined ? String(hijo.props.value) : label,
        label,
        disabled: groupDisabled || !!hijo.props.disabled,
        group,
      });
    }
  });
  return out;
}

function useMovil() {
  const leer = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(MQ_MOVIL).matches : false);
  const [movil, setMovil] = useState(leer);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(MQ_MOVIL);
    const on = () => setMovil(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return movil;
}

const Chevron = ({ abierto }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ flexShrink: 0, transform:abierto ? 'rotate(180deg)' : 'none' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const Check = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const Lupa = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const Select = forwardRef(function Select({
  value,
  defaultValue,
  onChange,
  options,
  children,
  placeholder,
  disabled = false,
  required = false,
  id,
  name,
  form,
  className = '',
  size = 'md',
  searchable,
  clearable = false,
  title,
  zIndexClass,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'data-testid': testId,
}, ref) {
  const baseId = useId().replace(/:/g, '');
  const listboxId = `${baseId}-lista`;
  const errorId = `${baseId}-error`;
  const tituloId = `${baseId}-titulo`;
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const buscarRef = useRef(null);
  const listaRef = useRef(null);
  useImperativeHandle(ref, () => triggerRef.current);

  // ── Opciones ──
  const { lista, placeholderHijo } = useMemo(() => {
    const crudas = options
      ? options.map((o) => ({ value: o.value, label: String(o.label ?? o.value), disabled: !!o.disabled, group: o.group }))
      : opcionesDeHijos(children, undefined, false, []);
    // <option value="" disabled>Texto</option> hace de placeholder.
    const ph = crudas.find((o) => String(o.value) === '' && o.disabled);
    return { lista: crudas.filter((o) => o !== ph), placeholderHijo: ph?.label };
  }, [options, children]);
  const textoPlaceholder = placeholder ?? placeholderHijo ?? 'Seleccionar…';
  const conBuscador = searchable ?? lista.length > BUSCADOR_DESDE;

  // ── Valor (controlado o no) ──
  const controlado = value !== undefined;
  const [interno, setInterno] = useState(defaultValue ?? '');
  const actual = controlado ? value : interno;
  const idxElegido = lista.findIndex((o) => String(o.value) === String(actual ?? ''));
  const elegido = idxElegido >= 0 ? lista[idxElegido] : null;

  // ── Estado de apertura ──
  const movil = useMovil();
  const [abierto, setAbierto] = useState(false);
  const [consulta, setConsulta] = useState('');
  const [activo, setActivo] = useState(-1); // índice en `visibles`
  const [pos, setPos] = useState(null);
  const [invalido, setInvalido] = useState(false);
  const typeahead = useRef({ buf: '', t: 0 });

  const visibles = useMemo(() => {
    if (!conBuscador || !consulta.trim()) return lista;
    const q = normalizar(consulta.trim());
    return lista.filter((o) => normalizar(o.label).includes(q) || normalizar(o.group).includes(q));
  }, [lista, consulta, conBuscador]);

  const habilitada = (i) => i >= 0 && i < visibles.length && !visibles[i].disabled;
  const primeraHabilitada = (desde = 0, paso = 1) => {
    for (let i = desde; i >= 0 && i < visibles.length; i += paso) if (!visibles[i].disabled) return i;
    return -1;
  };
  const mover = (desde, paso) => {
    const n = primeraHabilitada(desde + paso, paso);
    return n === -1 ? desde : n;
  };

  const abrir = useCallback((inicial) => {
    if (disabled) return;
    setConsulta('');
    setAbierto(true);
    setActivo(inicial);
  }, [disabled]);

  const cerrar = useCallback((devolverFoco = true) => {
    setAbierto(false);
    setConsulta('');
    typeahead.current.buf = '';
    if (devolverFoco) triggerRef.current?.focus();
  }, []);

  const elegir = (o) => {
    if (!o || o.disabled) return;
    if (!controlado) setInterno(o.value);
    setInvalido(false);
    onChange?.(o.value);
    cerrar(true);
  };
  const limpiar = () => {
    if (!controlado) setInterno('');
    onChange?.('');
    cerrar(true);
  };

  // Índice de apertura: el elegido, o la primera opción habilitada.
  const indiceInicial = () => (idxElegido >= 0 && !lista[idxElegido].disabled
    ? idxElegido : lista.findIndex((o) => !o.disabled));

  // Al filtrar, la activa pasa a la primera coincidencia.
  useEffect(() => {
    if (abierto && conBuscador) setActivo(primeraHabilitada(0, 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consulta]);

  // ── Posición del popover (escritorio) ──
  const medir = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth; const vh = window.innerHeight;
    const ancho = Math.min(Math.max(r.width, 200), vw - 16);
    const alto = Math.min(conBuscador ? 360 : 320, vh - 16);
    const abajo = vh - r.bottom - 8; const arriba = r.top - 8;
    const haciaArriba = abajo < Math.min(alto, 200) && arriba > abajo;
    const left = Math.max(8, Math.min(r.left, vw - ancho - 8));
    setPos({
      left, width: ancho,
      maxHeight: Math.max(120, Math.min(alto, haciaArriba ? arriba : abajo)),
      ...(haciaArriba ? { bottom: vh - r.top + 4 } : { top: r.bottom + 4 }),
      haciaArriba,
    });
  }, [conBuscador]);

  useLayoutEffect(() => {
    if (!abierto || movil) return undefined;
    medir();
    window.addEventListener('resize', medir);
    window.addEventListener('scroll', medir, true);
    // Además, cada cuadro revisa si el botón se movió sin evento de scroll
    // (modal que termina de animar, layout que cambia, scroll programático).
    let ultimo = '';
    let raf = 0;
    const vigilar = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      const firma = r ? `${r.top}|${r.left}|${r.width}|${r.height}` : '';
      if (firma !== ultimo) { ultimo = firma; medir(); }
      raf = requestAnimationFrame(vigilar);
    };
    raf = requestAnimationFrame(vigilar);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [abierto, movil, medir]);

  // ── Clic afuera, foco al buscador, bloqueo de scroll de la hoja ──
  useEffect(() => {
    if (!abierto) return undefined;
    const afuera = (e) => {
      if (triggerRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      cerrar(false);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('touchstart', afuera, { passive: true });
    let t;
    if (conBuscador) t = setTimeout(() => buscarRef.current?.focus(), 0);
    let prevOverflow;
    if (movil) { prevOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; }
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('touchstart', afuera);
      if (movil) document.body.style.overflow = prevOverflow;
    };
  }, [abierto, conBuscador, movil, cerrar]);

  // ── La opción activa siempre a la vista ──
  useEffect(() => {
    if (!abierto || activo < 0) return;
    const el = document.getElementById(`${baseId}-op-${activo}`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [abierto, activo, baseId, visibles]);

  // ── Teclado ──
  const buscarPorLetras = (tecla) => {
    const ta = typeahead.current;
    const ahora = Date.now();
    ta.buf = (ahora - ta.t > 600 ? '' : ta.buf) + normalizar(tecla);
    ta.t = ahora;
    const desde = abierto ? activo : idxElegido;
    const orden = visibles.map((_, i) => i);
    // Misma letra repetida: rota entre las que empiezan con ella.
    const unaSola = ta.buf.split('').every((c) => c === ta.buf[0]);
    const prefijo = unaSola ? ta.buf[0] : ta.buf;
    const inicio = unaSola || desde < 0 ? desde + 1 : desde;
    const rotado = [...orden.slice(Math.max(0, inicio)), ...orden.slice(0, Math.max(0, inicio))];
    return rotado.find((i) => !visibles[i].disabled && normalizar(visibles[i].label).startsWith(prefijo)) ?? -1;
  };

  const detener = (e) => { e.preventDefault(); e.stopPropagation(); };

  const onKeyNavegacion = (e, enBuscador) => {
    const k = e.key;
    if (k === 'ArrowDown') { detener(e); setActivo((a) => (a < 0 ? primeraHabilitada(0, 1) : mover(a, 1))); return true; }
    if (k === 'ArrowUp') {
      detener(e);
      if (e.altKey) { if (habilitada(activo)) elegir(visibles[activo]); else cerrar(true); return true; }
      setActivo((a) => (a < 0 ? primeraHabilitada(visibles.length - 1, -1) : mover(a, -1)));
      return true;
    }
    if (!enBuscador && k === 'Home') { detener(e); setActivo(primeraHabilitada(0, 1)); return true; }
    if (!enBuscador && k === 'End') { detener(e); setActivo(primeraHabilitada(visibles.length - 1, -1)); return true; }
    if (k === 'PageDown') { detener(e); setActivo((a) => { let n = a; for (let s = 0; s < 10; s += 1) n = mover(n, 1); return n; }); return true; }
    if (k === 'PageUp') { detener(e); setActivo((a) => { let n = a; for (let s = 0; s < 10; s += 1) n = mover(n, -1); return n; }); return true; }
    if (k === 'Enter') { detener(e); if (habilitada(activo)) elegir(visibles[activo]); return true; }
    if (k === 'Escape') { detener(e); cerrar(true); return true; }
    return false;
  };

  const onKeyTrigger = (e) => {
    if (disabled) return;
    const k = e.key;
    if (!abierto) {
      if (k === 'Enter' || k === ' ' || k === 'ArrowDown' || k === 'ArrowUp') {
        e.preventDefault();
        abrir(k === 'ArrowUp' && idxElegido < 0 ? primeraHabilitada(lista.length - 1, -1) : indiceInicial());
        return;
      }
      if (clearable && (k === 'Delete' || k === 'Backspace') && elegido) { e.preventDefault(); limpiar(); return; }
      if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && k !== ' ') {
        e.preventDefault();
        if (conBuscador) { abrir(-1); setConsulta(k); return; }
        const i = buscarPorLetras(k);
        abrir(i >= 0 ? i : indiceInicial());
      }
      return;
    }
    // Abierto sin buscador: el foco se queda en el botón.
    if (k === 'Tab') { cerrar(false); return; }
    if (k === ' ' && !typeahead.current.buf) { detener(e); if (habilitada(activo)) elegir(visibles[activo]); return; }
    if (onKeyNavegacion(e, false)) return;
    if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      const i = buscarPorLetras(k);
      if (i >= 0) setActivo(i);
    }
  };

  const onKeyBuscador = (e) => {
    if (e.key === 'Tab') { detener(e); cerrar(true); return; }
    onKeyNavegacion(e, true);
  };

  // ── Etiquetas ──
  // Se calcula en el render (no en un efecto) para que la hoja no pinte un
  // cuadro sin título antes de leer el <label>.
  let tituloHoja = '';
  if (abierto && typeof document !== 'undefined') {
    tituloHoja = title || ariaLabel || '';
    if (!tituloHoja && ariaLabelledBy) tituloHoja = ariaLabelledBy.split(/\s+/).map((x) => document.getElementById(x)?.textContent || '').join(' ').trim();
    if (!tituloHoja && id) {
      const sel = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
      tituloHoja = document.querySelector(`label[for="${sel}"]`)?.textContent?.trim() || '';
    }
    tituloHoja = tituloHoja || textoPlaceholder;
  }

  const alto = size === 'sm' ? 40 : 44;
  const altoOpcion = movil ? 44 : 40;
  // Inset lateral de la lista. En la hoja de teléfono el título y la X están
  // a 16 px del borde; las opciones quedaban con el texto a 16 px pero el
  // resaltado (margen 4) pegado al borde, y la activa se veía salida del
  // bloque (auditoría móvil de horario, 2026-09-19, H12). Con margen 8 +
  // padding 8 el texto sigue a 16 px y el resaltado respira igual que el
  // resto de la hoja. En escritorio el popover es angosto: se deja como
  // estaba (margen 4 + padding 12).
  const margenOpcion = movil ? '0 8px' : '0 4px';
  const padOpcion = movil ? '0 8px' : '0 12px';
  const padGrupo = movil ? '10px 16px 4px' : '10px 12px 4px';
  const activoId = abierto && activo >= 0 ? `${baseId}-op-${activo}` : undefined;
  const describedBy = [ariaDescribedBy, invalido ? errorId : null].filter(Boolean).join(' ') || undefined;

  // ── Lista (misma para popover y hoja) ──
  const filas = [];
  let grupoActual; let grupoFilas = null; let gi = 0;
  const cerrarGrupo = () => {
    if (grupoFilas) {
      const hid = `${baseId}-g-${gi}`;
      filas.push(
        <div role="group" aria-labelledby={hid} key={hid}>
          <div role="presentation" id={hid} className="text-xs font-semibold uppercase"
            style={{ padding: padGrupo, color: C.mutedFg, letterSpacing: '0.04em' }}>{grupoActual}</div>
          {grupoFilas}
        </div>,
      );
      gi += 1;
    }
    grupoFilas = null;
  };
  visibles.forEach((o, i) => {
    const sel = elegido != null && String(o.value) === String(elegido.value);
    const act = i === activo;
    const fila = (
      <div
        key={`${o.group ?? ''}|${String(o.value)}|${i}`}
        id={`${baseId}-op-${i}`}
        role="option"
        aria-selected={sel}
        aria-disabled={o.disabled || undefined}
        onMouseMove={() => { if (!o.disabled && activo !== i) setActivo(i); }}
        onClick={() => elegir(o)}
        className={`flex items-center text-sm select-none ${o.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        style={{
          minHeight: altoOpcion, padding: padOpcion, gap: 8, borderRadius: 8, margin: margenOpcion,
          color: sel ? C.primary : C.fg,
          fontWeight: sel ? 600 : 400,
          opacity: o.disabled ? 0.45 : 1,
          background: act && !o.disabled ? (sel ? suave(16) : C.muted) : (sel ? suave(9) : 'transparent'),
          outline: act && !o.disabled ? `1px solid ${suave(35)}` : 'none',
          outlineOffset: -1,
        }}
      >
        <span className="flex-1 min-w-0" style={{ overflowWrap: 'anywhere', padding: '6px 0' }}>{o.label}</span>
        <span style={{ width: 16, display: 'inline-flex' }}>{sel ? <Check /> : null}</span>
      </div>
    );
    if (o.group) {
      if (o.group !== grupoActual || !grupoFilas) { cerrarGrupo(); grupoActual = o.group; grupoFilas = []; }
      grupoFilas.push(fila);
    } else {
      cerrarGrupo(); grupoActual = undefined;
      filas.push(fila);
    }
  });
  cerrarGrupo();

  const buscador = conBuscador ? (
    <div style={{ padding: 8, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
      <div className="flex items-center rounded-lg" style={{ gap: 8, padding: '0 10px', minHeight: 40, border: `1px solid ${C.input}`, background: C.bg, color: C.mutedFg }}>
        <Lupa />
        <input
          ref={buscarRef}
          type="text"
          role="combobox"
          aria-label="Buscar opción"
          aria-expanded="true"
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activoId}
          autoComplete="off"
          spellCheck={false}
          value={consulta}
          onChange={(e) => setConsulta(e.target.value)}
          onKeyDown={onKeyBuscador}
          placeholder="Buscar…"
          className="flex-1 min-w-0 bg-transparent text-sm focus:outline-none"
          style={{ color: C.fg, border: 0, padding: '8px 0', boxShadow: 'none', fontSize: movil ? 16 : undefined }}
        />
      </div>
    </div>
  ) : null;

  const listbox = (
    <div
      ref={listaRef}
      id={listboxId}
      role="listbox"
      aria-label={tituloHoja || undefined}
      tabIndex={-1}
      className="overflow-y-auto overscroll-contain"
      style={{ padding: '4px 0', flex: '1 1 auto', minHeight: 0 }}
    >
      {clearable && elegido && !consulta && (
        <div
          role="presentation"
          onClick={limpiar}
          className="flex items-center text-sm cursor-pointer hover:underline"
          style={{ minHeight: altoOpcion, padding: padOpcion, margin: margenOpcion, color: C.mutedFg }}
        >
          Quitar selección
        </div>
      )}
      {filas}
      {visibles.length === 0 && (
        <div className="text-sm" role="presentation" style={{ padding: movil ? '12px 16px' : '12px', color: C.mutedFg }}>Sin resultados</div>
      )}
    </div>
  );

  const zStyle = zIndexClass ? undefined : 'var(--vls-select-z, 1000)';
  // Evita que el clic en el panel le quite el foco al botón o al buscador.
  const retenerFoco = (e) => { if (e.target !== buscarRef.current) e.preventDefault(); };

  let panel = null;
  if (abierto && typeof document !== 'undefined') {
    panel = movil ? (
      <div className={`fixed inset-0 flex flex-col justify-end ${zIndexClass || ''}`} style={{ zIndex: zStyle, margin: 0 }}>
        <div className="absolute inset-0 bg-black/40" aria-hidden="true" onMouseDown={() => cerrar(true)} />
        <div
          ref={panelRef}
          data-vls-select-panel="hoja"
          onMouseDown={retenerFoco}
          className="relative w-full flex flex-col rounded-t-2xl shadow-2xl"
          style={{ background: C.card, borderTop: `1px solid ${C.border}`, maxHeight: '80dvh', paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}
        >
          <div className="flex justify-center" style={{ paddingTop: 8, paddingBottom: 4 }} aria-hidden="true">
            <div className="rounded-full" style={{ width: 40, height: 5, background: C.border }} />
          </div>
          <div className="flex items-center justify-between" style={{ gap: 12, padding: '4px 8px 8px 16px' }}>
            <h2 id={tituloId} className="text-base font-semibold truncate" style={{ color: C.fg, margin: 0 }}>{tituloHoja}</h2>
            <button
              type="button" aria-label="Cerrar lista" onClick={() => cerrar(true)}
              className="flex items-center justify-center rounded-lg hover:bg-black/5"
              style={{ width: 44, height: 44, color: C.mutedFg, flexShrink: 0 }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          {buscador}
          {listbox}
        </div>
      </div>
    ) : (
      <div
        ref={panelRef}
        data-vls-select-panel="popover"
        onMouseDown={retenerFoco}
        className={`fixed flex flex-col border rounded-xl shadow-lg ${zIndexClass || ''}`}
        style={{
          zIndex: zStyle, margin: 0,
          background: C.card, borderColor: C.border,
          boxShadow: '0 10px 30px -8px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.06)',
          left: pos?.left ?? -9999, top: pos?.top, bottom: pos?.bottom, width: pos?.width,
          maxHeight: pos?.maxHeight ?? 320, visibility: pos ? 'visible' : 'hidden',
        }}
      >
        {buscador}
        {listbox}
      </div>
    );
  }

  const valorForm = elegido ? String(elegido.value) : '';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-controls={listboxId}
        aria-activedescendant={conBuscador ? undefined : activoId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={describedBy}
        aria-required={required || undefined}
        aria-invalid={invalido || undefined}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        data-testid={testId}
        data-vls-select=""
        onClick={() => (abierto ? cerrar(true) : abrir(indiceInicial()))}
        onKeyDown={onKeyTrigger}
        className={`inline-flex items-center text-sm text-left rounded-lg border focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`}
        style={{
          minWidth: 0, height: alto, padding: '0 10px 0 12px', gap: 8,
          background: disabled ? C.muted : C.bg,
          borderColor: invalido ? C.error : (abierto ? C.primary : C.input),
          color: elegido ? C.fg : C.mutedFg,
          opacity: disabled ? 0.65 : 1,
          boxShadow: abierto ? `0 0 0 3px ${suave(15)}` : undefined,
          transition: 'border-color 150ms ease-out, box-shadow 150ms ease-out',
        }}
      >
        <span className="flex-1 min-w-0 truncate">{elegido ? elegido.label : textoPlaceholder}</span>
        <span style={{ color: C.mutedFg, display: 'inline-flex' }}><Chevron abierto={abierto} /></span>
      </button>
      {required ? (
        // Un input oculto no se valida: este proxy sí, y bloquea el submit nativo.
        <input
          tabIndex={-1}
          aria-hidden="true"
          name={name}
          form={form}
          required
          value={valorForm}
          onChange={() => {}}
          onInvalid={(e) => { e.preventDefault(); setInvalido(true); triggerRef.current?.focus(); }}
          style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none', border: 0, padding: 0 }}
        />
      ) : name ? (
        <input type="hidden" name={name} form={form} value={valorForm} />
      ) : null}
      {invalido && (
        <span id={errorId} role="alert" className="block text-xs" style={{ color: C.error, marginTop: 4 }}>
          Seleccione una opción.
        </span>
      )}
      {panel && createPortal(panel, document.body)}
    </>
  );
});

export default Select;
